import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { WebSocketContext } from "../context/WebSockectContext";
import api from "../context/api";
import "../styles/Chat.css";

const Chat = () => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const { ws, sendMessage } = useContext(WebSocketContext);
  const { token, phoneNumber } = useContext(AuthContext);
  const navigate = useNavigate();
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for incoming WebSocket messages
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle chat messages - check if it's for this user's phone number
        if (data.type === 'chat_message' && data.phoneNumber === phoneNumber) {
          setMessages((prevMessages) => {
            // Avoid duplicates by checking _id, message content, and timestamp
            const exists = prevMessages.some(msg => 
              msg._id === data._id || 
              (msg.message === data.message && msg.createdAt === data.createdAt)
            );
            if (exists) return prevMessages;
            return [...prevMessages, data];
          });
          scrollToBottom();
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  }, [ws, phoneNumber]);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      return;
    }

    const newMessage = {
      message: message.trim(),
      senderRole: "user",
      phoneNumber: phoneNumber || "unknown",
      senderToken: token,
      createdAt: new Date().toISOString(),
    };

    // Send via WebSocket
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(newMessage));
    }

    try {
      await api.post("/userPost", newMessage);
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  useEffect(() => {
    const fetchPreviousMessages = async () => {
      try {
        const response = await api.get(`/user/messages/${phoneNumber || "unknown"}`);
        setMessages(response.data);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    if (phoneNumber) {
      fetchPreviousMessages();
    }
  }, [phoneNumber, setMessages]);

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      try {
        await api.delete(`/messages/delete/${messageId}`);
        setMessages((prevMessages) =>
          prevMessages.filter((msg) => msg._id !== messageId)
        );
      } catch (error) {
        alert("Failed to delete the message.");
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h2>Chat with Huntex Admin</h2>
      </div>

      <div className="chat-messages">
        {messages.map((item, index) => (
          <div
            key={item._id || index}
            className={`message ${item.senderRole === "admin" ? "admin-message" : "user-message"}`}
            onContextMenu={(e) => {
              e.preventDefault();
              handleDeleteMessage(item._id);
            }}
          >
            <div className="message-text">{item.message}</div>
            <div className="message-time">
              {item.createdAt
                ? new Date(item.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      <div className="chat-input-container">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          className="chat-input"
        />
        <button onClick={handleSendMessage} className="send-button">
          Send
        </button>
      </div>
    </div>
  );
};

export default Chat;
